import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BadgeCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, VOUCH_TAGS } from "@/lib/api";

export function OfficialVouchDialog({ open, onOpenChange, user, onDone }) {
  const [tags, setTags] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTags([]);
      setNote("");
    }
  }, [open]);

  if (!user) return null;
  const uid = user.user_id;

  const toggleTag = (t) => {
    setTags((prev) => {
      if (prev.includes(t)) return prev.filter((x) => x !== t);
      if (prev.length >= 2) return prev; // max 2
      return [...prev, t];
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.officialVouch(uid, {
        voucher_name: "Amoreska Team",
        relationship: "platform",
        tags,
        note: note.trim(),
      });
      toast.success("Official vouch added");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="official-vouch-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BadgeCheck className="h-5 w-5 text-primary" /> Official Amoreska vouch
          </DialogTitle>
          <DialogDescription>
            Add an auto-approved platform stamp for{" "}
            <span className="text-foreground font-medium">{user.name || uid}</span>. From “Amoreska Team”.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">Tags <span className="text-muted-foreground font-normal">(pick up to 2)</span></Label>
            <div className="flex flex-wrap gap-2" data-testid="vouch-tags">
              {VOUCH_TAGS.map((t) => {
                const active = tags.includes(t);
                const disabled = !active && tags.length >= 2;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTag(t)}
                    disabled={disabled}
                    data-testid={`vouch-tag-${t}`}
                    className={`rounded-full px-3 py-1.5 text-sm ring-1 transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground ring-primary"
                        : disabled
                        ? "opacity-40 ring-border/50 cursor-not-allowed"
                        : "bg-secondary/40 ring-border/60 hover:ring-primary/40"
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="vouch-note">Note <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea id="vouch-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="A short note about this member…" data-testid="vouch-note" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="vouch-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><BadgeCheck className="h-4 w-4" /> Add official vouch</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
